import React, { useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import { formatSpan, formatSubathonClock } from '../../hooks/useSubathonAdmin';
import { SubathonEventType, SubathonState } from '../../types/subathon';
import {
  Button,
  Input,
  Label,
  Muted,
  PanelGrid,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
} from '../ui';

interface Props {
  state: SubathonState;
  remainingMs: number;
  run: (request: Promise<{ data: any }>) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const Clock = styled.div<{ $urgent?: boolean; $idle?: boolean }>`
  font-family: 'Orbitron', 'Segoe UI', sans-serif;
  font-size: clamp(2.6rem, 7vw, 4.6rem);
  font-weight: 800;
  letter-spacing: 3px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ $urgent, $idle }) =>
    $idle ? theme.colors.textMuted : $urgent ? '#ff4d6d' : '#fff'};
  text-shadow: ${({ $idle }) => ($idle ? 'none' : '0 0 24px rgba(255, 20, 147, 0.45)')};
`;

const ClockSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 170px;
`;

const ClockRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.86rem;
  color: ${theme.colors.textMuted};

  strong {
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const StartRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: flex-end;
  flex-wrap: wrap;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  input {
    width: 110px;
  }
`;

const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 0.75rem;
`;

const QuickCard = styled.div`
  border: 1px solid rgba(255, 20, 147, 0.16);
  border-radius: 12px;
  padding: 0.75rem;
  background: rgba(255, 20, 147, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const QuickTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #fff;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;

  span {
    color: ${theme.colors.pinkSoft};
    font-weight: 600;
    font-size: 0.74rem;
  }
`;

const QuickRow = styled.div`
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  grid-template-areas:
    'qty name'
    'send send';
  gap: 0.4rem;
  align-items: center;

  input:first-of-type {
    grid-area: qty;
  }

  input:last-of-type {
    grid-area: name;
  }

  button {
    grid-area: send;
    width: 100%;
  }
`;

const Feed = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 520px;
  overflow: auto;
`;

const FeedItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.45rem 0.6rem;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.03);
  font-size: 0.86rem;

  small {
    color: ${theme.colors.textMuted};
    font-size: 0.74rem;
  }

  strong {
    color: #9dffb0;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

const StatePill = styled.span<{ $tone: 'live' | 'idle' | 'warn' }>`
  align-self: flex-start;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'live'
        ? 'rgba(157, 255, 176, 0.35)'
        : $tone === 'warn'
          ? 'rgba(255, 196, 87, 0.4)'
          : 'rgba(255, 255, 255, 0.18)'};
  background: ${({ $tone }) =>
    $tone === 'live'
      ? 'rgba(157, 255, 176, 0.1)'
      : $tone === 'warn'
        ? 'rgba(255, 196, 87, 0.1)'
        : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ $tone }) =>
    $tone === 'live' ? '#9dffb0' : $tone === 'warn' ? '#ffc457' : theme.colors.textMuted};
`;

/** Cargas que no se pueden automatizar y Euge suma a mano mientras transmite. */
const QUICK_LOADS: {
  type: SubathonEventType;
  title: string;
  unitLabel: string;
  hint: (state: SubathonState) => string;
  step?: number;
}[] = [
  {
    type: 'cafecito',
    title: 'Cafecito',
    unitLabel: 'cafecitos',
    hint: (s) => `${s.rules.cafecitoUnit} = ${formatSeconds(s.rules.cafecitoSeconds)}`,
  },
  {
    type: 'chest',
    title: 'Cofre de Streamloots',
    unitLabel: 'cofres',
    hint: (s) => `1 = ${formatSeconds(s.rules.chestSeconds)}. El nombre de Twitch acredita las cartas.`,
  },
  {
    type: 'coffee',
    title: 'Ko-fi a mano',
    unitLabel: 'cafés',
    hint: (s) => `${s.rules.coffeeUnit} = ${formatSeconds(s.rules.coffeeSeconds)}`,
  },
  {
    type: 'tip',
    title: 'Propina a mano',
    unitLabel: 'monto',
    hint: (s) => `${s.rules.tipUnit} = ${formatSeconds(s.rules.tipSeconds)}`,
    step: 0.5,
  },
];

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} seg`;
  const minutes = seconds / 60;
  return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `hace ${hours} h`;
}

const SubathonControlTab: React.FC<Props> = ({ state, remainingMs, run, refresh }) => {
  const { timer, totals, recent } = state;

  const [startMinutes, setStartMinutes] = useState(String(Math.round(timer.initialMs / 60000) || 120));
  const [adjustMinutes, setAdjustMinutes] = useState('5');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const active = timer.status === 'running' || timer.status === 'paused';
  const urgent = active && remainingMs > 0 && remainingMs < 5 * 60 * 1000;

  const act = async (request: Promise<{ data: any }>) => {
    setBusy(true);
    const ok = await run(request);
    setBusy(false);
    return ok;
  };

  const start = () => {
    const minutes = Math.max(0, parseInt(startMinutes, 10) || 0);
    act(api.post('/subathon/timer/start', { startMinutes: minutes }));
  };

  const adjust = (sign: 1 | -1) => {
    const minutes = Math.abs(parseFloat(adjustMinutes) || 0);
    if (!minutes) return;
    act(api.post('/subathon/events', { type: 'manual', minutes: minutes * sign }));
  };

  const quickLoad = async (type: SubathonEventType) => {
    const quantity = parseFloat(quantities[type] || '1') || 0;
    if (quantity <= 0) return;
    const ok = await act(
      api.post('/subathon/events', { type, quantity, displayName: names[type] || '' }),
    );
    if (ok) {
      setQuantities((prev) => ({ ...prev, [type]: '' }));
      setNames((prev) => ({ ...prev, [type]: '' }));
    }
  };

  const finish = () => {
    if (!window.confirm('¿Finalizar el extensible? El contador se detiene y queda en el historial.')) return;
    act(api.post('/subathon/timer/finish'));
  };

  const reset = () => {
    if (!window.confirm('¿Dejar todo en cero para el próximo extensible? El historial se conserva.')) return;
    act(api.post('/subathon/timer/reset'));
  };

  const stateLabel = () => {
    if (timer.status === 'idle') return 'Sin extensible';
    if (timer.status === 'finished') return 'Finalizado';
    if (timer.waiting) return 'Esperando el primer aporte';
    if (timer.status === 'paused') return 'En pausa';
    return 'En vivo';
  };

  return (
    <PanelGrid $cols="minmax(0, 1.15fr) minmax(0, 1fr)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Contador</SurfaceTitle>
            <StatePill $tone={timer.status === 'running' ? 'live' : timer.waiting || timer.status === 'paused' ? 'warn' : 'idle'}>
              {stateLabel()}
            </StatePill>
          </SurfaceHeader>
          <SurfaceBody>
            <ClockRow>
              <Clock $urgent={urgent} $idle={!active}>
                {formatSubathonClock(remainingMs)}
              </Clock>
              <ClockSide>
                <Stat>
                  <span>Arranque</span>
                  <strong>{formatSpan(timer.initialMs)}</strong>
                </Stat>
                <Stat>
                  <span>Sumado</span>
                  <strong>{formatSpan(timer.addedMs)}</strong>
                </Stat>
                <Stat>
                  <span>Aportes</span>
                  <strong>{totals.events}</strong>
                </Stat>
                {timer.capMs > 0 && (
                  <Stat>
                    <span>Tope</span>
                    <strong>{formatSpan(timer.capMs)}</strong>
                  </Stat>
                )}
              </ClockSide>
            </ClockRow>

            {timer.capReached && (
              <Muted style={{ marginTop: '0.6rem', color: '#ffc457' }}>
                Se alcanzó el tope: lo que llegue de más queda registrado pero no suma tiempo.
              </Muted>
            )}
            {timer.waiting && (
              <Muted style={{ marginTop: '0.6rem' }}>
                Arrancó en cero. El primer aporte pone el reloj en marcha.
              </Muted>
            )}

            {!active ? (
              <>
                <StartRow style={{ marginTop: '1rem' }}>
                  <div>
                    <Label>Arrancar con (minutos)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={startMinutes}
                      onChange={(e) => setStartMinutes(e.target.value)}
                    />
                  </div>
                  <Button disabled={busy || !state.enabled} onClick={start}>
                    Iniciar extensible
                  </Button>
                  {timer.status === 'finished' && (
                    <Button $variant="ghost" disabled={busy} onClick={reset}>
                      Limpiar para el próximo
                    </Button>
                  )}
                </StartRow>
                <Muted style={{ marginTop: '0.5rem' }}>
                  En 0 el contador queda esperando: arranca cuando entre el primer aporte. El tope se
                  toma de Reglas.
                </Muted>
              </>
            ) : (
              <>
                <Controls>
                  {timer.status === 'running' ? (
                    <Button $variant="ghost" disabled={busy} onClick={() => act(api.post('/subathon/timer/pause'))}>
                      Pausar
                    </Button>
                  ) : (
                    <Button
                      disabled={busy || timer.waiting}
                      onClick={() => act(api.post('/subathon/timer/resume'))}
                    >
                      Reanudar
                    </Button>
                  )}
                  <Button $variant="danger" disabled={busy} onClick={finish}>
                    Finalizar
                  </Button>
                </Controls>

                <StartRow style={{ marginTop: '0.85rem' }}>
                  <div>
                    <Label>Ajuste a mano (minutos)</Label>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      value={adjustMinutes}
                      onChange={(e) => setAdjustMinutes(e.target.value)}
                    />
                  </div>
                  <Button $size="sm" disabled={busy} onClick={() => adjust(1)}>
                    Sumar
                  </Button>
                  <Button $size="sm" $variant="ghost" disabled={busy} onClick={() => adjust(-1)}>
                    Restar
                  </Button>
                </StartRow>
              </>
            )}
          </SurfaceBody>
        </Surface>

        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Cargas a mano</SurfaceTitle>
          </SurfaceHeader>
          <SurfaceBody>
            {!active ? (
              <Muted>Iniciá el extensible para poder cargar cafecitos, cofres o propinas.</Muted>
            ) : (
              <>
                <Muted style={{ marginTop: 0, marginBottom: '0.7rem' }}>
                  Poné la cantidad y el tiempo lo calcula solo con lo que configuraste en Reglas. El
                  nombre es opcional y aparece en el aviso del overlay.
                </Muted>
                <QuickGrid>
                  {QUICK_LOADS.map((item) => (
                    <QuickCard key={item.type}>
                      <QuickTitle>
                        {item.title}
                        <span>{item.hint(state)}</span>
                      </QuickTitle>
                      <QuickRow>
                        <Input
                          type="number"
                          min={0}
                          step={item.step || 1}
                          placeholder={item.unitLabel}
                          value={quantities[item.type] || ''}
                          onChange={(e) =>
                            setQuantities((prev) => ({ ...prev, [item.type]: e.target.value }))
                          }
                        />
                        <Input
                          placeholder="Nombre (opcional)"
                          value={names[item.type] || ''}
                          onChange={(e) => setNames((prev) => ({ ...prev, [item.type]: e.target.value }))}
                        />
                        <Button $size="sm" disabled={busy} onClick={() => quickLoad(item.type)}>
                          Sumar
                        </Button>
                      </QuickRow>
                    </QuickCard>
                  ))}
                </QuickGrid>
              </>
            )}
          </SurfaceBody>
        </Surface>
      </div>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Últimos aportes</SurfaceTitle>
          {totals.seconds > 0 && <Muted style={{ margin: 0 }}>{formatSpan(totals.seconds * 1000)} en total</Muted>}
        </SurfaceHeader>
        <SurfaceBody>
          {recent.length === 0 ? (
            <Muted>Todavía no entró nada. Acá vas a ver cada aporte con el tiempo que sumó.</Muted>
          ) : (
            <Feed>
              {recent.map((event) => (
                <FeedItem key={event.id}>
                  <div>
                    <div>{event.label}</div>
                    <small>
                      {timeAgo(event.createdAt)} · {event.source === 'manual' ? 'a mano' : event.source}
                      {event.secondsCapped > 0 && ' · recortado por el tope'}
                    </small>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>
                      {event.secondsAdded >= 0 ? '+' : ''}
                      {formatSeconds(Math.abs(event.secondsAdded))}
                    </strong>
                    <Button
                      $size="sm"
                      $variant="ghost"
                      disabled={busy}
                      title="Deshacer este aporte y devolver el tiempo"
                      onClick={async () => {
                        await act(api.post(`/subathon/events/${event.id}/undo`));
                        await refresh();
                      }}
                    >
                      Deshacer
                    </Button>
                  </div>
                </FeedItem>
              ))}
            </Feed>
          )}
        </SurfaceBody>
      </Surface>
    </PanelGrid>
  );
};

export default SubathonControlTab;
